import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Loader2, Pin, Upload } from 'lucide-react';
import { API_BASE } from '../config';
import { useLocale } from '../contexts/LocaleContext';
import { useToast } from '../contexts/ToastContext';
import { cn } from '../lib/utils';
import { toBcp47Locale, type SupportedLocale } from '../locales/locale';
import { getResourceKind } from '../utils/resource-kind';
import { linkResourceToEntity, uploadImageResourceToS3 } from '../utils/imageResources';
import { Button } from './ui/button';
import { HoverCard, HoverCardContent, HoverCardTrigger } from './ui/hover-card';
import { Separator } from './ui/separator';
import { ButtonTooltip, TooltipProvider } from './ui/tooltip';
import { S3ResourceImage } from './S3ResourceImage';

export interface LinkedImageResource {
  id: string;
  title: string;
  fileName?: string | null;
  mimeType: string | null;
  s3Key: string | null;
  url: string | null;
  naturalId?: string | null;
  resourceDate?: string | null;
  createdAt?: string | null;
  tags?: string[] | null;
}

function formatResourceDate(iso: string, locale: SupportedLocale): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return new Intl.DateTimeFormat(toBcp47Locale(locale), {
    dateStyle: 'short',
  }).format(date);
}

export type LinkedImageEntityType = 'character' | 'location';

interface LinkedImageResourcesCarouselProps {
  projectId: string;
  entityType: LinkedImageEntityType;
  entityId: string;
  documentId: string;
  token: string;
  refreshKey?: number;
  uploadEnabled?: boolean;
  mainImageResourceId?: string | null;
  onMainImageChange?: (resourceId: string | null) => Promise<boolean> | boolean;
  onLinked?: () => void;
  className?: string;
}

function isImageFile(file: File): boolean {
  return file.type.startsWith('image/');
}

function sortImagesWithMainFirst(
  resources: LinkedImageResource[],
  mainImageResourceId: string | null | undefined,
): LinkedImageResource[] {
  if (!mainImageResourceId) {
    return resources;
  }
  const mainIndex = resources.findIndex((resource) => resource.id === mainImageResourceId);
  if (mainIndex <= 0) {
    return resources;
  }
  const next = [...resources];
  const [main] = next.splice(mainIndex, 1);
  next.unshift(main);
  return next;
}

export const LinkedImageResourcesCarousel: React.FC<LinkedImageResourcesCarouselProps> = ({
  projectId,
  entityType,
  entityId,
  documentId,
  token,
  refreshKey = 0,
  uploadEnabled = true,
  mainImageResourceId = null,
  onMainImageChange,
  onLinked,
  className,
}) => {
  const { t, locale } = useLocale();
  const { showToast, showErrorToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [images, setImages] = useState<LinkedImageResource[]>([]);
  const [index, setIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isPinning, setIsPinning] = useState(false);

  const orderedImages = useMemo(
    () => sortImagesWithMainFirst(images, mainImageResourceId),
    [images, mainImageResourceId],
  );
  const slideCount = orderedImages.length + 1;
  const isUploadSlide = index >= orderedImages.length;
  const hasNavigation = slideCount > 1;
  const currentImage = !isUploadSlide ? orderedImages[index] : null;
  const isCurrentMain = Boolean(currentImage && currentImage.id === mainImageResourceId);
  const currentResourceHref = currentImage
    ? `/projects/${projectId}/resources/${currentImage.id}`
    : null;

  const currentHoverRows = useMemo(() => {
    if (!currentImage) {
      return [] as { label: string; value: string }[];
    }
    const rows: { label: string; value: string }[] = [
      { label: t('title'), value: currentImage.title },
    ];
    if (currentImage.fileName?.trim() && currentImage.fileName.trim() !== currentImage.title) {
      rows.push({ label: t('File name'), value: currentImage.fileName.trim() });
    }
    if (currentImage.naturalId?.trim()) {
      rows.push({ label: t('Natural ID'), value: currentImage.naturalId.trim() });
    }
    if (currentImage.resourceDate?.trim()) {
      rows.push({
        label: t('reference_date_label'),
        value: formatResourceDate(currentImage.resourceDate.trim(), locale),
      });
    }
    if (currentImage.createdAt?.trim()) {
      rows.push({
        label: t('creation_date'),
        value: formatResourceDate(currentImage.createdAt.trim(), locale),
      });
    }
    return rows;
  }, [currentImage, t, locale]);

  const entityLocalePrefix = entityType === 'location' ? 'location' : 'character';

  const loadImages = useCallback(async (): Promise<LinkedImageResource[]> => {
    const url = new URL(`${API_BASE}/api/projects/${projectId}/resources`);
    if (entityType === 'location') {
      url.searchParams.set('locationIds', entityId);
    } else {
      url.searchParams.set('characterIds', entityId);
    }
    const response = await fetch(url.toString(), {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      return [];
    }
    const data = (await response.json()) as { resources?: LinkedImageResource[] };
    return (data.resources ?? []).filter((resource) => getResourceKind(resource) === 'image');
  }, [projectId, entityType, entityId, token]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const next = await loadImages();
        if (!cancelled) {
          setImages(next);
          setIndex((current) => Math.min(current, next.length));
        }
      } catch {
        if (!cancelled) {
          setImages([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadImages, refreshKey]);

  const handlePinClick = useCallback(
    async (event: React.MouseEvent): Promise<void> => {
      event.preventDefault();
      event.stopPropagation();
      if (!currentImage || !onMainImageChange || isPinning || !uploadEnabled) {
        return;
      }
      const nextMainId = isCurrentMain ? null : currentImage.id;
      setIsPinning(true);
      try {
        const ok = await onMainImageChange(nextMainId);
        if (ok && nextMainId) {
          setIndex(0);
        }
      } finally {
        setIsPinning(false);
      }
    },
    [currentImage, onMainImageChange, isPinning, uploadEnabled, isCurrentMain],
  );

  const goPrevious = useCallback(() => {
    setIndex((current) => (current - 1 + slideCount) % slideCount);
  }, [slideCount]);

  const goNext = useCallback(() => {
    setIndex((current) => (current + 1) % slideCount);
  }, [slideCount]);

  const uploadFiles = useCallback(
    async (files: File[]): Promise<void> => {
      const imageFiles = files.filter(isImageFile);
      if (imageFiles.length === 0) {
        showErrorToast(t('character_linked_images_images_only'));
        return;
      }
      if (!uploadEnabled || isUploading) {
        return;
      }

      setIsUploading(true);
      let uploadedCount = 0;
      let firstNewIndex: number | null = null;
      try {
        for (const file of imageFiles) {
          const { id } = await uploadImageResourceToS3({
            file,
            projectId,
            token,
          });
          await linkResourceToEntity({
            projectId,
            token,
            resourceId: id,
            entityType,
            entityId,
            documentId,
          });
          uploadedCount += 1;
        }
        const next = await loadImages();
        setImages(next);
        firstNewIndex = Math.max(0, next.length - uploadedCount);
        setIndex(firstNewIndex);
        onLinked?.();
        showToast(
          uploadedCount === 1
            ? t(`${entityLocalePrefix}_linked_image_uploaded`)
            : t(`${entityLocalePrefix}_linked_images_uploaded`).replace('{count}', String(uploadedCount)),
        );
      } catch {
        showErrorToast(t('failed_to_upload_image'));
      } finally {
        setIsUploading(false);
      }
    },
    [
      uploadEnabled,
      isUploading,
      projectId,
      token,
      entityType,
      entityId,
      entityLocalePrefix,
      documentId,
      loadImages,
      onLinked,
      showToast,
      showErrorToast,
      t,
    ],
  );

  const openFilePicker = (): void => {
    if (!uploadEnabled || isUploading) {
      return;
    }
    fileInputRef.current?.click();
  };

  const label = t('character_linked_images_n_of_m')
    .replace('{n}', String(index + 1))
    .replace('{m}', String(slideCount));

  return (
    <div
      className={cn(
        'group relative h-52 w-52 shrink-0 overflow-hidden rounded-lg border border-border bg-muted sm:h-64 sm:w-64',
        className,
      )}
      data-testid="character-linked-images-carousel"
      aria-label={t('character_linked_images')}
    >
      {isUploadSlide ? (
        <div
          className={cn(
            'flex h-full w-full cursor-pointer flex-col items-center justify-center gap-2 border-2 border-dashed p-3 text-center transition-colors',
            isDragging ? 'border-primary bg-muted' : 'border-muted-foreground/25 bg-card',
            (!uploadEnabled || isUploading) && 'cursor-default opacity-70',
          )}
          onDragOver={(event) => {
            event.preventDefault();
            if (uploadEnabled && !isUploading) {
              setIsDragging(true);
            }
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(event) => {
            event.preventDefault();
            setIsDragging(false);
            const files = Array.from(event.dataTransfer.files);
            void uploadFiles(files);
          }}
          onClick={openFilePicker}
          role="button"
          tabIndex={uploadEnabled ? 0 : -1}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              openFilePicker();
            }
          }}
          aria-label={t('character_linked_images_upload')}
        >
          {isUploading ? (
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          ) : (
            <Upload className="h-8 w-8 text-muted-foreground" />
          )}
          <p className="text-xs font-medium leading-snug text-foreground">
            {isUploading ? t('character_linked_images_uploading') : t('character_linked_images_upload')}
          </p>
          {!isUploading ? (
            <p className="text-[0.625rem] leading-snug text-muted-foreground">
              {t('character_linked_images_upload_hint')}
            </p>
          ) : null}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            disabled={!uploadEnabled || isUploading}
            onChange={(event) => {
              const files = Array.from(event.target.files ?? []);
              event.target.value = '';
              void uploadFiles(files);
            }}
          />
        </div>
      ) : currentImage && currentResourceHref ? (
        <HoverCard openDelay={250} closeDelay={100}>
          <HoverCardTrigger asChild>
            <Link
              to={currentResourceHref}
              className="flex h-full w-full items-center justify-center no-underline"
              aria-label={currentImage.title}
            >
              <S3ResourceImage
                projectId={projectId}
                resourceId={currentImage.id}
                token={token}
                title={currentImage.title}
                alt={currentImage.title}
                className="h-full w-full object-contain"
              />
            </Link>
          </HoverCardTrigger>
          <HoverCardContent
            side="left"
            align="start"
            className="w-72 max-w-[min(18rem,calc(100vw-2rem))]"
          >
            <div className="space-y-2.5 text-sm">
              {currentHoverRows.map((row) => (
                <div key={row.label}>
                  <div className="text-xs font-medium text-muted-foreground">{row.label}</div>
                  <div className="break-words text-foreground">{row.value}</div>
                </div>
              ))}
              <Separator />
              <Link
                to={currentResourceHref}
                className="inline-flex text-sm font-medium text-foreground underline-offset-4 hover:underline"
              >
                {t('character_linked_image_open_resource')}
              </Link>
            </div>
          </HoverCardContent>
        </HoverCard>
      ) : null}
      {currentImage && uploadEnabled && onMainImageChange ? (
        <div
          className={cn(
            'absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 transition-opacity',
            isCurrentMain ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
          )}
        >
          <TooltipProvider delayDuration={300}>
            <ButtonTooltip tooltip={t(`${entityLocalePrefix}_main_image_pin_tooltip`)} side="top">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className={cn(
                  'h-9 w-9 rounded-full bg-card/90 shadow-sm hover:bg-card',
                  isCurrentMain && 'text-foreground',
                )}
                onClick={(event) => {
                  void handlePinClick(event);
                }}
                disabled={isPinning}
                aria-label={t(`${entityLocalePrefix}_main_image_pin_tooltip`)}
                aria-pressed={isCurrentMain}
              >
                {isPinning ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Pin className={cn('h-4 w-4', isCurrentMain && 'fill-current')} />
                )}
              </Button>
            </ButtonTooltip>
          </TooltipProvider>
        </div>
      ) : null}
      {hasNavigation ? (
        <>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute left-1 top-1/2 h-8 w-8 -translate-y-1/2 rounded-full bg-card/90 shadow-sm hover:bg-card"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              goPrevious();
            }}
            aria-label={t('previous')}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2 rounded-full bg-card/90 shadow-sm hover:bg-card"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              goNext();
            }}
            aria-label={t('next')}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <span className="pointer-events-none absolute bottom-1 left-1/2 -translate-x-1/2 rounded bg-card/90 px-1.5 py-0.5 text-xs text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100">
            {label}
          </span>
        </>
      ) : null}
    </div>
  );
};
